import type { Ref } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Brain, Footprints, MessageSquare, PlayCircle, Route as RouteIcon, type LucideIcon } from "lucide-react";
import type { NowSessionDto } from "../../api/now";
import {
  mainChoices, moreWays, routeKey, routeSize, routeSteps, toolRoute,
  type DeeperItem, type DeeperRoute, type QuestionKey,
} from "../../lib/diveDeeper";
import { Skeleton } from "../ui/skeleton";
import { cn } from "../../lib/utils";

interface Props {
  item: DeeperItem;
  reflective: boolean;
  questionKey: QuestionKey;
  /** null until /api/now/start answers (or when it failed). */
  session: NowSessionDto | null;
  failed: boolean;
  busy: boolean;
  headingRef?: Ref<HTMLHeadingElement>;
  onRun: (route: DeeperRoute) => void;
}

const rowIcon = (r: DeeperRoute): LucideIcon => {
  switch (r.route) {
    case "operator":
    case "mm_general":
      return Brain;
    case "activity":
      return PlayCircle;
    case "next_step":
      return Footprints;
    case "question":
      return MessageSquare;
    default:
      return RouteIcon;
  }
};
const rowKey = (r: DeeperRoute) => (r.route === "activity" ? `activity:${r.refId}` : routeKey(r));
const isRoute = (r: DeeperRoute | null): r is DeeperRoute => r !== null;

interface CardProps {
  title: string;
  line: string;
  size: string;
  badge?: string;
  usual?: string;
  small?: boolean;
  disabled: boolean;
  onClick: () => void;
}

/** A choice is a button whose name is its title, line and size (spec §6). */
const ChoiceCard = ({ title, line, size, badge, usual, small, disabled, onClick }: CardProps) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={cn(
      "block w-full min-h-11 rounded-2xl border p-3 text-start motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60",
      badge ? "border-primary/40 bg-primary/[0.04]" : "border-border hover:border-primary/40"
    )}
  >
    <span className="flex items-start justify-between gap-2">
      <span className={cn("font-medium text-foreground", small ? "text-sm" : "text-[15px]")}>{title}</span>
      {badge && <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary">{badge}</span>}
    </span>
    {usual && <span className="block text-xs text-primary">{usual}</span>}
    <span className="block text-sm text-foreground/80">{line}</span>
    {size && <span className="block text-xs text-foreground/70">{size}</span>}
  </button>
);

/**
 * The guiding question, the main choices (rendered at once from the item), "Short on time?"
 * and "More ways" (from the session) — spec 2026-11-04 §2.3 parts 2–5.
 */
const DiveChoices = ({ item, reflective, questionKey, session, failed, busy, headingRef, onRun }: Props) => {
  const { t } = useTranslation();
  const { main, alternatives } = mainChoices(item, { reflective });
  const quick = session?.quick ?? null;
  const quickRoute = quick ? toolRoute(quick) : null;
  const usual = session?.usual ?? null;
  // Exactly one Suggested: quick when the session says so, else the learned main card, else the first.
  const badge =
    session?.suggested === "quick" && quickRoute
      ? "quick"
      : usual && main.some((r) => routeKey(r) === usual)
        ? usual
        : routeKey(main[0]);
  const usualOn = (key: string) =>
    Boolean(usual) && badge === key && (key === usual || (key === "quick" && quickRoute !== null && routeKey(quickRoute) === usual));
  const shown = [...main, ...(quickRoute ? [quickRoute] : [])];
  const rows = session
    ? moreWays({ alternatives, more: session.more.map(toolRoute).filter(isRoute), shown })
    : failed
      ? moreWays({ alternatives, more: [], shown: main })
      : null;

  const sizeText = (r: DeeperRoute, minutes?: number) => {
    const s = routeSize(r, minutes);
    const steps = routeSteps(r, session?.main);
    const parts: string[] = [];
    if (steps) parts.push(t("now.dive.size.steps", { count: steps }));
    if (s.minutes) parts.push(t(s.about ? "now.dive.size.about" : "now.dive.size.min", { min: s.minutes }));
    if (s.opens) parts.push(t(`now.dive.size.opens.${s.opens}`));
    return parts.join(" · ");
  };

  const rowLabel = (r: DeeperRoute) => {
    switch (r.route) {
      case "operator":
        return t(`mm_op_${r.operator}_title__guided`);
      case "activity":
        return t("now.dive.more.activityNamed", { label: r.label });
      case "mm_general":
        return t("now.dive.more.mm_general");
      case "next_step":
        return t("now.dive.more.next_step");
      case "question":
        return t("now.dive.more.question");
      default:
        return t(`now.dive.card.${routeKey(r)}.title`);
    }
  };

  return (
    <div className="space-y-4">
      <h2 ref={headingRef} tabIndex={-1} className="text-lg font-semibold text-foreground focus:outline-none">
        {t(`now.dive.question.${questionKey}`)}
      </h2>

      <div className="space-y-2">
        {main.map((r) => {
          const key = routeKey(r);
          return (
            <ChoiceCard
              key={key}
              title={t(`now.dive.card.${key}.title`)}
              line={t(`now.dive.card.${key}.line`)}
              size={sizeText(r)}
              badge={badge === key ? t("now.dive.suggested") : undefined}
              usual={usualOn(key) ? t("now.dive.usual") : undefined}
              disabled={busy}
              onClick={() => onRun(r)}
            />
          );
        })}
      </div>

      {!session && !failed ? (
        <Skeleton className="h-16 w-full rounded-2xl" aria-hidden />
      ) : quick && quickRoute ? (
        <section className="space-y-2" aria-labelledby="now-quick">
          <p id="now-quick" className="text-xs font-semibold uppercase tracking-widest text-foreground/70">
            {t("now.dive.quickTitle")}
          </p>
          <ChoiceCard
            small
            title={rowLabel(quickRoute)}
            line={quick.why}
            size={sizeText(quickRoute, quick.minutes)}
            badge={badge === "quick" ? t("now.dive.suggested") : undefined}
            usual={usualOn("quick") ? t("now.dive.usual") : undefined}
            disabled={busy}
            onClick={() => onRun(quickRoute)}
          />
        </section>
      ) : null}

      <section className="space-y-1" aria-labelledby="now-more">
        <p id="now-more" className="text-xs font-semibold uppercase tracking-widest text-foreground/70">
          {t("now.dive.moreTitle")}
        </p>
        {rows === null ? (
          <div className="space-y-1" aria-hidden>
            <Skeleton className="h-11 w-full rounded-xl" />
            <Skeleton className="h-11 w-full rounded-xl" />
          </div>
        ) : (
          <ul className="space-y-1">
            {rows.map((r) => {
              const Icon = rowIcon(r);
              return (
                <li key={rowKey(r)}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onRun(r)}
                    className="flex min-h-11 w-full items-center gap-2 rounded-xl px-2 text-start text-sm text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60"
                  >
                    <Icon className="h-4 w-4 shrink-0 text-foreground/70" aria-hidden />
                    <span className="flex-1">{rowLabel(r)}</span>
                    <ArrowRight className="h-4 w-4 shrink-0 rtl:rotate-180" aria-hidden />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

export default DiveChoices;
